import { apiService } from './api';
import { contentService } from './contentService';
import {
  LevelStatusDto,
  LevelAdvancementDto,
  LevelRequirementDto,
  ContentRecommendationDto,
  ProgressRecordDto,
  ProgressResponseDto,
  ActivityType,
} from '../types';

/** API payload from GET /api/LevelProgression/content or /content/level/{n} */
interface FilteredContentPayload {
  level?: number;
  Level?: number;
  vocabulary?: Array<Record<string, unknown>>;
  Vocabulary?: Array<Record<string, unknown>>;
  grammarLessons?: Array<Record<string, unknown>>;
  GrammarLessons?: Array<Record<string, unknown>>;
  dialogues?: Array<Record<string, unknown>>;
  Dialogues?: Array<Record<string, unknown>>;
  quizzes?: Array<Record<string, unknown>>;
  Quizzes?: Array<Record<string, unknown>>;
}

export interface KnowledgePlacementRecommendationDto {
  courseId: number;
  courseTitle: string;
  levelId: number;
  lessonId: number;
  lessonQuizId: number;
  quizTitle: string;
  contextSubtitle: string;
  evaluatedAt: string;
}

export interface KnowledgePlacementStateDto {
  shouldShowDashboardBanner: boolean;
  hasParticipated: boolean;
  lastOutcome?: 'recommended' | 'advanced' | 'opt_out' | string | null;
  lastRunId?: number | null;
  lastCourseId?: number | null;
  lastUpdatedAt?: string | null;
  lastRecommendation?: KnowledgePlacementRecommendationDto | null;
}

/**
 * Level Progression Service
 * 
 * Handles all level progression related operations including:
 * - Level status checking and advancement processing
 * - Content filtering by user level
 * - Progress tracking and statistics
 * - Level advancement criteria validation
 */
export class LevelProgressionService {
  private readonly baseEndpoint = '/api/LevelProgression';

  /**
   * Get current level status for the authenticated user
   * Requirements: 7.1 - Level status checking
   */
  async getLevelStatus(): Promise<LevelStatusDto> {
    try {
      // API returns the data directly, not wrapped in ApiResponse
      const response = await apiService.get<LevelStatusDto>(
        `${this.baseEndpoint}/status`
      );
      // Handle both wrapped and direct responses
      if (response && 'data' in response && response.data) {
        return response.data as LevelStatusDto;
      }
      return response as LevelStatusDto;
    } catch (error) {
      console.error('Failed to fetch level status:', error);
      throw new Error('Failed to fetch user level status');
    }
  }

  /**
   * Evaluate if user is eligible for level advancement
   * Requirements: 7.2 - Level advancement evaluation
   */
  async evaluateLevelAdvancement(): Promise<LevelAdvancementDto> {
    try {
      const response = await apiService.post<ApiResponse<LevelAdvancementDto>>(
        `${this.baseEndpoint}/evaluate`
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to evaluate level advancement:', error);
      throw new Error('Failed to evaluate level advancement');
    }
  }

  /**
   * Promote user to next level
   * Requirements: 7.3 - Level promotion processing
   */
  async promoteToNextLevel(): Promise<LevelAdvancementDto> {
    try {
      const response = await apiService.post<ApiResponse<LevelAdvancementDto>>(
        `${this.baseEndpoint}/promote`
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to promote user level:', error);
      throw new Error('Failed to promote user level');
    }
  }

  /**
   * Get level-appropriate content for the user.
   * Maps FilteredContentDto (vocabulary, grammar, dialogues, quizzes) into dashboard cards.
   */
  async getLevelContent(level?: number): Promise<ContentRecommendationDto[]> {
    try {
      const endpoint =
        level != null
          ? `${this.baseEndpoint}/content/level/${level}`
          : `${this.baseEndpoint}/content`;

      const response = await apiService.get<unknown>(endpoint, { cache: false });
      const payload =
        response && typeof response === 'object' && 'data' in response && (response as { data?: unknown }).data
          ? (response as { data: FilteredContentPayload }).data
          : (response as FilteredContentPayload);

      return this.mapFilteredContentToRecommendations(payload);
    } catch (error) {
      console.error('Failed to fetch level content:', error);
      throw new Error('Failed to fetch level content');
    }
  }

  private mapFilteredContentToRecommendations(payload: FilteredContentPayload | null | undefined): ContentRecommendationDto[] {
    if (!payload || typeof payload !== 'object') return [];

    const defaultLevel = (payload.level ?? payload.Level ?? 1) as number;

    const vocabulary = (payload.vocabulary ?? payload.Vocabulary ?? []).map((item) =>
      this.toRecommendation(ActivityType.VOCABULARY, item, defaultLevel, 5)
    );
    const grammar = (payload.grammarLessons ?? payload.GrammarLessons ?? []).map((item) =>
      this.toRecommendation(ActivityType.GRAMMAR, item, defaultLevel, 15)
    );
    const dialogues = (payload.dialogues ?? payload.Dialogues ?? []).map((item) =>
      this.toRecommendation(ActivityType.DIALOGUE, item, defaultLevel, 10)
    );
    const quizzes = (payload.quizzes ?? payload.Quizzes ?? []).map((item) =>
      this.toRecommendation(ActivityType.QUIZ, item, defaultLevel, 10)
    );

    return this.interleaveRecommendations([grammar, vocabulary, dialogues, quizzes]);
  }

  private toRecommendation(
    type: ActivityType,
    item: Record<string, unknown>,
    defaultLevel: number,
    estimatedDuration: number
  ): ContentRecommendationDto {
    const title =
      (item.title as string | undefined) ??
      (item.Title as string | undefined) ??
      (item.englishTranslation as string | undefined) ??
      (item.EnglishTranslation as string | undefined) ??
      (item.persianWord as string | undefined) ??
      (item.PersianWord as string | undefined) ??
      type;

    return {
      type,
      title,
      level: (item.level as number | undefined) ?? (item.Level as number | undefined) ?? defaultLevel,
      estimatedDuration,
      contentId: (item.id as number | undefined) ?? (item.Id as number | undefined),
    };
  }

  /** Mix content types so the dashboard grid is varied (grammar → vocab → dialogue → quiz). */
  private interleaveRecommendations(groups: ContentRecommendationDto[][]): ContentRecommendationDto[] {
    const maxLen = Math.max(0, ...groups.map((g) => g.length));
    const merged: ContentRecommendationDto[] = [];
    for (let i = 0; i < maxLen; i += 1) {
      for (const group of groups) {
        if (group[i]) merged.push(group[i]);
      }
    }
    return merged;
  }

  /**
   * When legacy level content is empty, suggest in-progress or accessible courses.
   */
  private async getCourseFallbackRecommendations(currentLevel: number): Promise<ContentRecommendationDto[]> {
    const courses = await contentService.getAvailableCourses().catch(() => []);
    if (!courses.length) return [];

    const ranked = [...courses].sort((a, b) => {
      const aProgress = a.userProgressPercentage ?? 0;
      const bProgress = b.userProgressPercentage ?? 0;
      const aInProgress = aProgress > 0 && !a.isCompleted;
      const bInProgress = bProgress > 0 && !b.isCompleted;
      if (aInProgress !== bInProgress) return aInProgress ? -1 : 1;
      if (a.isAccessible !== b.isAccessible) return a.isAccessible ? -1 : 1;
      return bProgress - aProgress;
    });

    return ranked
      .filter((c) => c.isAccessible && !c.isCompleted)
      .slice(0, 6)
      .map((course) => ({
        type: ActivityType.LESSONS,
        title: course.title,
        level: currentLevel,
        estimatedDuration: Math.max(10, Math.min(45, (course.lessonCount || 1) * 5)),
        contentId: course.id,
      }));
  }

  /**
   * Get progression statistics for the user
   * Requirements: 7.5 - Progression statistics
   */
  async getProgressionStats(): Promise<{
    currentLevel: number;
    totalXP: number;
    progressToNext: number;
    completedActivities: Record<ActivityType, number>;
    averageScores: Record<ActivityType, number>;
    timeSpentByActivity: Record<ActivityType, number>;
    levelHistory: Array<{
      level: number;
      achievedAt: string;
      timeSpent: number;
    }>;
  }> {
    try {
      const response = await apiService.get<any>(`${this.baseEndpoint}/stats`, { cache: false });
      // Handle both wrapped and direct responses
      const payload =
        response && 'data' in response && response.data ? response.data : response;
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid progression stats response');
      }
      const stats = payload as Record<string, unknown>;
      const totalXP =
        (stats.totalXP as number | undefined) ??
        (stats.TotalXP as number | undefined) ??
        (stats.totalXp as number | undefined) ??
        0;
      return { ...stats, totalXP } as Awaited<ReturnType<LevelProgressionService['getProgressionStats']>>;
    } catch (error) {
      console.error('Failed to fetch progression stats:', error);
      throw new Error('Failed to fetch progression stats');
    }
  }

  /**
   * Detailed progress + usage for profile. Optional `courseIds` limits Time/Activity-related stats to those courses (hourly pattern stays account-wide).
   */
  async getProfileProgressDetail(courseIds?: number[] | null): Promise<any> {
    try {
      const { resolveIntlTimeZoneId } = await import('../utils/resolveIntlTimeZone');
      const clientTz = resolveIntlTimeZoneId(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const params = new URLSearchParams();
      if (clientTz) params.set('activityTimeZone', clientTz);
      if (courseIds && courseIds.length > 0) {
        params.set('courseIds', courseIds.join(','));
      }
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await apiService.get<any>(`${this.baseEndpoint}/progress-detail${qs}`, {
        cache: false,
        timeout: 60000
      });
      if (!response || typeof response !== 'object') {
        return null;
      }
      // Unwrap if API client or gateway nested the payload
      const root =
        response.data !== undefined && response.data !== null && typeof response.data === 'object'
          ? response.data
          : response;
      const stats = (root as any).stats ?? (root as any).Stats;
      if (!stats || typeof stats !== 'object') {
        return null;
      }
      return root;
    } catch {
      return null;
    }
  }

  /** Hourly time-of-day activity from live action logs (7, 30, or 180 day lookback). */
  async getHourlyActivityPattern(lookbackDays: 7 | 30 | 180 = 30): Promise<any[]> {
    try {
      const { resolveIntlTimeZoneId } = await import('../utils/resolveIntlTimeZone');
      const clientTz = resolveIntlTimeZoneId(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const params = new URLSearchParams();
      params.set('hourlyLookbackDays', String(lookbackDays));
      if (clientTz) params.set('activityTimeZone', clientTz);
      const response = await apiService.get<any>(
        `${this.baseEndpoint}/progress-detail?${params.toString()}`,
        { cache: false, timeout: 60000 }
      );
      const root =
        response?.data !== undefined && response?.data !== null && typeof response.data === 'object'
          ? response.data
          : response;
      const pattern =
        (root as any)?.hourlyActivityPattern ??
        (root as any)?.HourlyActivityPattern;
      return Array.isArray(pattern) ? pattern : [];
    } catch {
      return [];
    }
  }

  /**
   * Get advancement criteria for a specific level
   * Requirements: 7.6 - Level advancement criteria
   */
  async getLevelCriteria(level: number): Promise<LevelRequirementDto> {
    try {
      const response = await apiService.get<LevelRequirementDto>(
        `${this.baseEndpoint}/criteria/${level}`
      );
      // Handle both wrapped and direct responses
      if (response && 'data' in response && response.data) {
        return response.data as LevelRequirementDto;
      }
      return response as LevelRequirementDto;
    } catch (error) {
      console.error('Failed to fetch level criteria:', error);
      throw new Error('Failed to fetch level criteria');
    }
  }

  /**
   * Record progress for an activity
   * Used internally by other services to track progress
   */
  async recordProgress(progressData: ProgressRecordDto): Promise<ProgressResponseDto> {
    try {
      const response = await apiService.post<ApiResponse<ProgressResponseDto>>(
        `${this.baseEndpoint}/record-progress`,
        progressData
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to record progress:', error);
      throw new Error('Failed to record progress');
    }
  }

  /**
   * Check if user can access content at a specific level
   * Requirements: 7.4 - Content filtering by user level
   */
  async canAccessLevel(targetLevel: number): Promise<boolean> {
    try {
      const levelStatus = await this.getLevelStatus();
      return levelStatus.currentLevel >= targetLevel;
    } catch (error) {
      console.error('Failed to check level access:', error);
      return false;
    }
  }

  /**
   * Get filtered content recommendations based on user's current level
   * Requirements: 7.4 - Content filtering by user level
   * @param activityType - Optional filter by activity type
   * @param cachedLevelStatus - Optional pre-fetched level status to avoid duplicate /status API call
   */
  async getRecommendedContent(
    activityType?: ActivityType,
    cachedLevelStatus?: LevelStatusDto
  ): Promise<ContentRecommendationDto[]> {
    try {
      const levelStatus = cachedLevelStatus ?? (await this.getLevelStatus());
      let allContent = await this.getLevelContent(levelStatus.currentLevel).catch(() => [] as ContentRecommendationDto[]);

      if (!allContent.length) {
        allContent = await this.getCourseFallbackRecommendations(levelStatus.currentLevel);
      }

      if (activityType) {
        return allContent.filter((content) => content.type === activityType);
      }

      return allContent;
    } catch (error) {
      console.error('Failed to get recommended content:', error);
      throw new Error('Failed to get recommended content');
    }
  }

  /**
   * Calculate progress percentage to next level
   * Requirements: 7.1, 7.5 - Level status and progression statistics
   * @param cached - Optional pre-fetched levelStatus and stats to avoid duplicate /status and /stats API calls
   */
  async getProgressToNextLevel(cached?: {
    levelStatus: LevelStatusDto;
    stats: { completedActivities: Record<ActivityType, number> };
  }): Promise<{
    currentLevel: number;
    nextLevel: number;
    progressPercentage: number;
    requirements: LevelRequirementDto;
    completedRequirements: Partial<LevelRequirementDto>;
  }> {
    try {
      const [levelStatus, stats] = cached
        ? [cached.levelStatus, cached.stats]
        : await Promise.all([this.getLevelStatus(), this.getProgressionStats()]);

      // Guard against undefined levelStatus
      if (!levelStatus || typeof levelStatus.nextLevel === 'undefined') {
        throw new Error('Level status is invalid');
      }
      
      const requirements = await this.getLevelCriteria(levelStatus.nextLevel);
      
      const completedRequirements: Partial<LevelRequirementDto> = {
        completedQuizzes: stats.completedActivities.quiz || 0,
        completedLessons: stats.completedActivities.lessons || 0
      };

      // Calculate overall progress percentage
      const quizProgress = Math.min(
        (completedRequirements.completedQuizzes || 0) / requirements.requiredQuizzes,
        1
      );
      const lessonProgress = Math.min(
        (completedRequirements.completedLessons || 0) / requirements.requiredLessons,
        1
      );
      
      const progressPercentage = Math.round(((quizProgress + lessonProgress) / 2) * 100);

      return {
        currentLevel: levelStatus.currentLevel,
        nextLevel: levelStatus.nextLevel,
        progressPercentage,
        requirements,
        completedRequirements
      };
    } catch (error) {
      console.error('Failed to calculate progress to next level:', error);
      throw new Error('Failed to calculate progress to next level');
    }
  }

  /**
   * Get level advancement history
   * Requirements: 7.5 - Progression statistics
   */
  async getLevelHistory(): Promise<Array<{
    level: number;
    achievedAt: string;
    timeSpent: number;
    activitiesCompleted: number;
  }>> {
    try {
      const stats = await this.getProgressionStats();
      // Handle missing activitiesCompleted field for backward compatibility
      return stats.levelHistory.map(h => ({
        ...h,
        activitiesCompleted: (h as any).activitiesCompleted ?? 0
      }));
    } catch (error) {
      console.error('Failed to get level history:', error);
      throw new Error('Failed to get level history');
    }
  }

  /**
   * Check if user meets requirements for a specific level
   * Requirements: 7.6 - Level advancement criteria
   */
  async meetsLevelRequirements(targetLevel: number): Promise<{
    meets: boolean;
    requirements: LevelRequirementDto;
    currentProgress: Partial<LevelRequirementDto>;
    missingRequirements: string[];
  }> {
    try {
      const [requirements, stats] = await Promise.all([
        this.getLevelCriteria(targetLevel),
        this.getProgressionStats()
      ]);

      const currentProgress: Partial<LevelRequirementDto> = {
        completedQuizzes: stats.completedActivities.quiz || 0,
        completedLessons: stats.completedActivities.lessons || 0
      };

      const missingRequirements: string[] = [];
      
      if ((currentProgress.completedQuizzes || 0) < requirements.requiredQuizzes) {
        const remaining = requirements.requiredQuizzes - (currentProgress.completedQuizzes || 0);
        missingRequirements.push(`${remaining} quiz${remaining > 1 ? 'es' : ''} remaining`);
      }
      
      if ((currentProgress.completedLessons || 0) < requirements.requiredLessons) {
        const remaining = requirements.requiredLessons - (currentProgress.completedLessons || 0);
        missingRequirements.push(`${remaining} lesson${remaining > 1 ? 's' : ''} remaining`);
      }

      // Check average score requirement if available
      const averageScore = Object.values(stats.averageScores).reduce((a, b) => a + b, 0) / 
                          Object.values(stats.averageScores).length;
      
      if (requirements.minimumScore && averageScore < requirements.minimumScore) {
        missingRequirements.push(`Average score must be at least ${requirements.minimumScore}`);
      }

      const meets = missingRequirements.length === 0;

      return {
        meets,
        requirements,
        currentProgress,
        missingRequirements
      };
    } catch (error) {
      console.error('Failed to check level requirements:', error);
      throw new Error('Failed to check level requirements');
    }
  }

  async getKnowledgePlacementState(): Promise<KnowledgePlacementStateDto> {
    const raw = await apiService.get<any>(`${this.baseEndpoint}/knowledge-placement/state`, { cache: false });
    return this.mapKnowledgePlacementState(raw);
  }

  async startKnowledgePlacementRun(courseId: number): Promise<{ runId: number; courseId: number; startedAt: string }> {
    const raw = await apiService.post<any>(`${this.baseEndpoint}/knowledge-placement/start`, { courseId });
    const r = (raw && raw.data && typeof raw.data === 'object') ? raw.data : raw;
    return {
      runId: Number(r?.runId ?? r?.RunId ?? 0),
      courseId: Number(r?.courseId ?? r?.CourseId ?? 0),
      startedAt: String(r?.startedAt ?? r?.StartedAt ?? '')
    };
  }

  private mapKnowledgePlacementState(rawRoot: any): KnowledgePlacementStateDto {
    const r = (rawRoot && rawRoot.data && typeof rawRoot.data === 'object') ? rawRoot.data : rawRoot;
    const rec = r?.lastRecommendation ?? r?.LastRecommendation;
    return {
      shouldShowDashboardBanner: Boolean(r?.shouldShowDashboardBanner ?? r?.ShouldShowDashboardBanner),
      hasParticipated: Boolean(r?.hasParticipated ?? r?.HasParticipated),
      lastOutcome: r?.lastOutcome ?? r?.LastOutcome ?? null,
      lastRunId: r?.lastRunId ?? r?.LastRunId ?? null,
      lastCourseId: r?.lastCourseId ?? r?.LastCourseId ?? null,
      lastUpdatedAt: r?.lastUpdatedAt ?? r?.LastUpdatedAt ?? null,
      lastRecommendation: rec ? {
        courseId: Number(rec.courseId ?? rec.CourseId ?? 0),
        courseTitle: String(rec.courseTitle ?? rec.CourseTitle ?? ''),
        levelId: Number(rec.levelId ?? rec.LevelId ?? 0),
        lessonId: Number(rec.lessonId ?? rec.LessonId ?? 0),
        lessonQuizId: Number(rec.lessonQuizId ?? rec.LessonQuizId ?? 0),
        quizTitle: String(rec.quizTitle ?? rec.QuizTitle ?? ''),
        contextSubtitle: String(rec.contextSubtitle ?? rec.ContextSubtitle ?? ''),
        evaluatedAt: String(rec.evaluatedAt ?? rec.EvaluatedAt ?? '')
      } : null
    };
  }

  async recordKnowledgePlacementAttempt(payload: {
    runId: number;
    lessonQuizId: number;
    decision: 'attempted' | 'skipped_easy';
    scorePercent?: number;
    passed?: boolean;
  }): Promise<KnowledgePlacementStateDto> {
    const raw = await apiService.post<any>(`${this.baseEndpoint}/knowledge-placement/attempt`, payload);
    return this.mapKnowledgePlacementState(raw);
  }

  async completeKnowledgePlacementRun(
    runId: number,
    outcome: 'advanced' | 'recommended' | 'opt_out',
    recommendedLessonQuizId?: number
  ): Promise<KnowledgePlacementStateDto> {
    const raw = await apiService.post<any>(`${this.baseEndpoint}/knowledge-placement/complete`, {
      runId,
      outcome,
      recommendedLessonQuizId
    });
    return this.mapKnowledgePlacementState(raw);
  }

  async dismissKnowledgePlacement(): Promise<KnowledgePlacementStateDto> {
    const raw = await apiService.post<any>(`${this.baseEndpoint}/knowledge-placement/dismiss`);
    return this.mapKnowledgePlacementState(raw);
  }
}

// Export singleton instance
export const levelProgressionService = new LevelProgressionService();